import { useEffect, useMemo, useState } from "react";
import GoogleAdsPanel from "./GoogleAdsPanel";
import SiteSummary from "./SiteSummary";
import InfoTip from "./InfoTip";
import { api } from "./lib/api";
import type { Site } from "./types";

type Metric = "pageViews" | "visitors";
type RangeDays = 7 | 30 | 90 | 365;

interface AnalyticsDay { date: string; pageViews: number; visitors: number }
interface BreakdownItem { label: string; pageViews: number }
interface AnalyticsData {
  range: { days: number; startDate: string; endDate: string };
  days: AnalyticsDay[];
  totals: { pageViews: number; visitors: number };
  previous: { pageViews: number; visitors: number };
  breakdowns: {
    pages: BreakdownItem[];
    devices: BreakdownItem[];
    sources: BreakdownItem[];
    countries: BreakdownItem[];
  };
  channels?: Array<{ channel: string; pageViews: number; visitors: number }>;
  campaigns?: Array<{ channel: string; campaign: string; pageViews: number }>;
  events?: Array<{ channel: string; event: string; total: number }>;
  clicks?: Array<{ kind: string; target: string; clicks: number }>;
  clickPages?: Array<{ page: string; clicks: number }>;
  clickGrid?: ClickGrid;
  freshness: { lastRecordedAt: string | null };
  privacy: string;
}

interface ClickGrid {
  page: string;
  device: string;
  columns: number;
  rows: number;
  cells: Array<{ gx: number; gy: number; clicks: number }>;
}

const ADS_CHANNELS = ["Google Ads", "Meta Ads"];
const ORGANIC_CHANNELS = ["Organico Google", "Organico altri motori"];
const CLICK_KIND_LABEL: Record<string, string> = {
  tel: "Telefono", whatsapp: "WhatsApp", email: "Email", form: "Modulo", download: "File",
  link: "Link", button: "Pulsante", other: "Altro",
};
const EVENT_LABEL: Record<string, string> = { tel: "Telefonate", whatsapp: "WhatsApp", form: "Moduli", email: "Email" };

function ChannelSection({ data }: { data: AnalyticsData }) {
  const channels = data.channels || [];
  const events = data.events || [];
  const total = channels.reduce((sum, item) => sum + item.visitors, 0);
  const group = (names: string[] | null) => {
    const included = (name: string) => names ? names.includes(name) : !ADS_CHANNELS.includes(name) && !ORGANIC_CHANNELS.includes(name);
    return {
      visitors: channels.filter((item) => included(item.channel)).reduce((sum, item) => sum + item.visitors, 0),
      contacts: events.filter((item) => included(item.channel)).reduce((sum, item) => sum + item.total, 0),
    };
  };
  const cards = [
    { title: "Da annunci", note: "Google Ads e Meta Ads", ...group(ADS_CHANNELS), accent: true },
    { title: "Organico", note: "Ricerche su Google e altri motori", ...group(ORGANIC_CHANNELS), accent: false },
    { title: "Diretto, social e altri siti", note: "Indirizzo digitato, social, WhatsApp, link da altri siti", ...group(null), accent: false },
  ];
  const contactsByChannel = (channel: string) => events.filter((item) => item.channel === channel).reduce((sum, item) => sum + item.total, 0);
  const eventTotals = ["tel", "whatsapp", "form", "email"].map((event) => ({
    event, total: events.filter((item) => item.event === event).reduce((sum, item) => sum + item.total, 0),
  })).filter((item) => item.total > 0);

  return <section className="analytics-channels">
    <header>
      <div><p className="eyebrow indigo">Annunci o organico?</p><h3>Da dove arrivano le persone e chi ti contatta</h3></div>
      <p>Il canale viene riconosciuto all'arrivo (annuncio, ricerca, social, indirizzo diretto) senza cookie e senza memorizzare dati personali.</p>
    </header>
    {channels.length ? <>
      <div className="analytics-channel-cards">
        {cards.map((card) => <article key={card.title} className={card.accent ? "ads" : ""}>
          <small>{card.title}</small>
          <strong>{integer.format(card.visitors)}</strong>
          <span>{total ? `${number.format(card.visitors / total * 100)}% dei visitatori` : "—"}</span>
          <em>{integer.format(card.contacts)} contatti</em>
          <i>{card.note}</i>
        </article>)}
      </div>
      <div className="analytics-channel-table" role="table" aria-label="Dettaglio per canale">
        <div role="row" className="head"><span>Canale</span><span>Visitatori</span><span>Visualizzazioni</span><span>Contatti</span></div>
        {channels.map((item) => <div role="row" key={item.channel}>
          <span><b className={ADS_CHANNELS.includes(item.channel) ? "ads" : ""} />{item.channel}</span>
          <span>{integer.format(item.visitors)}</span><span>{integer.format(item.pageViews)}</span><span>{integer.format(contactsByChannel(item.channel))}</span>
        </div>)}
      </div>
      <p className="analytics-footnote">
        {eventTotals.length ? `Contatti nel periodo: ${eventTotals.map((item) => `${integer.format(item.total)} ${EVENT_LABEL[item.event].toLowerCase()}`).join(", ")}. ` : "Nessun contatto ancora registrato nel periodo. "}
        Un visitatore resta nel canale da cui è arrivato per tutto il giorno. Gli annunci Google sono riconosciuti dalla marcatura automatica; per gli annunci Meta aggiungi <code>utm_source=meta&amp;utm_medium=paid_social</code> all'indirizzo dell'annuncio, altrimenti finiscono in «Social».
      </p>
    </> : <div className="analytics-empty"><strong>Il canale si legge dalle prossime visite</strong><p>Appena arrivano visite con la nuova rilevazione, qui vedrai quante vengono da annunci e quante dalla ricerca.</p></div>}
  </section>;
}

function ClickMap({ grid, pages, device, page, onDevice, onPage }: {
  grid: ClickGrid | undefined; pages: Array<{ page: string; clicks: number }>;
  device: string; page: string; onDevice: (value: string) => void; onPage: (value: string) => void;
}) {
  const max = Math.max(1, ...(grid?.cells.map((cell) => cell.clicks) || [1]));
  const total = grid?.cells.reduce((sum, cell) => sum + cell.clicks, 0) || 0;
  const byCell = new Map((grid?.cells || []).map((cell) => [`${cell.gx}-${cell.gy}`, cell.clicks]));
  const columns = grid?.columns || 10;
  const rows = grid?.rows || 20;
  return <section className="analytics-clickmap">
    <header>
      <div><h3>Mappa dei clic</h3><p>Dove si clicca nella pagina, dall'inizio (in alto) alla fine (in basso)</p></div>
      <div className="analytics-clickmap-controls">
        <select value={page} onChange={(event) => onPage(event.target.value)} aria-label="Pagina da analizzare">
          {pages.length ? pages.map((item) => <option key={item.page} value={item.page}>{item.page} ({integer.format(item.clicks)})</option>) : <option value="">Nessuna pagina</option>}
        </select>
        <div role="group" aria-label="Dispositivo">{["Mobile", "Desktop"].map((name) => <button key={name} className={device === name ? "active" : ""} onClick={() => onDevice(name)}>{name === "Mobile" ? "Telefono" : "Computer"}</button>)}</div>
      </div>
    </header>
    {total ? <>
      <div className={`analytics-clickgrid ${device.toLowerCase()}`} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }} role="img" aria-label={`Mappa dei clic su ${grid?.page}: ${total} clic`}>
        {Array.from({ length: rows * columns }, (_, index) => {
          const gx = index % columns;
          const gy = Math.floor(index / columns);
          const clicks = byCell.get(`${gx}-${gy}`) || 0;
          return <i key={index} title={clicks ? `${clicks} clic` : undefined} style={clicks ? { background: `rgba(79,70,229,${Math.max(0.14, clicks / max)})` } : undefined} />;
        })}
      </div>
      <p className="analytics-footnote">{integer.format(total)} clic su {grid?.page}. Ogni quadrato vale il 10% della larghezza e il 5% dell'altezza della pagina; più è scuro, più clic ha ricevuto. La posizione cambia con la lunghezza della pagina, quindi va letta come indicazione.</p>
    </> : <div className="analytics-empty"><strong>I clic compaiono dalle prossime visite</strong><p>Per ogni pagina vedrai in quali zone le persone toccano o cliccano, su telefono e computer. Si salvano solo posizione e tipo di elemento, mai testo scritto dal visitatore.</p></div>}
  </section>;
}

interface GoogleStatus { configured: boolean; connected: boolean }
interface GoogleMetrics { totals: { clicks: number; impressions: number; ctr: number; position: number } }
interface CloudflareData {
  configured: boolean;
  traffic: unknown | null;
  analyticsStatus?: "available" | "domain_missing" | "zone_not_found" | "query_failed";
}

interface ChartPoint {
  key: string;
  days: AnalyticsDay[];
  pageViews: number;
  visitors: number;
}

const number = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 1 });
const integer = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 });
const dateLong = new Intl.DateTimeFormat("it-IT", { weekday: "long", day: "numeric", month: "long" });
const dateShort = new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "short" });

function displayDate(value: string, formatter = dateLong) {
  return formatter.format(new Date(`${value}T12:00:00Z`));
}

function displayPeriod(days: AnalyticsDay[]) {
  if (!days.length) return "Seleziona un periodo";
  if (days.length === 1) return displayDate(days[0].date);
  return `${displayDate(days[0].date, dateShort)} - ${displayDate(days.at(-1)!.date, dateShort)}`;
}

function change(current: number, previous: number) {
  if (previous === 0) return current === 0 ? null : 100;
  return ((current - previous) / previous) * 100;
}

function Change({ current, previous }: { current: number; previous: number }) {
  // Senza dati nel periodo precedente un «+100%» sarebbe un numero inventato: lo diciamo a parole.
  if (previous === 0 && current > 0) return <span className="analytics-change neutral">Primo periodo con dati</span>;
  const value = change(current, previous);
  if (value === null) return <span className="analytics-change neutral">Nessuna variazione</span>;
  return <span className={`analytics-change ${value >= 0 ? "positive" : "negative"}`}>
    {value >= 0 ? "↑" : "↓"} {number.format(Math.abs(value))}% sul periodo precedente
  </span>;
}

function Breakdown({ title, subtitle, items, empty }: { title: string; subtitle: string; items: BreakdownItem[]; empty: string }) {
  const max = Math.max(1, ...items.map((item) => item.pageViews));
  return <section className="analytics-breakdown">
    <header><div><h3>{title}</h3><p>{subtitle}</p></div><span>{items.length ? `${items.length} voci` : "In raccolta"}</span></header>
    {items.length ? <div className="analytics-ranking">
      {items.map((item, index) => <div key={`${item.label}-${index}`}>
        <span className="analytics-rank">{String(index + 1).padStart(2, "0")}</span>
        <div><strong>{item.label}</strong><i><b style={{ width: `${Math.max(3, item.pageViews / max * 100)}%` }} /></i></div>
        <em>{integer.format(item.pageViews)}</em>
      </div>)}
    </div> : <div className="analytics-empty"><strong>I dettagli iniziano da oggi</strong><p>{empty}</p></div>}
  </section>;
}

export default function AnalyticsPanel({ site, onOpenSeo }: { site: Site; onOpenSeo: () => void }) {
  const [range, setRange] = useState<RangeDays>(30);
  const [metric, setMetric] = useState<Metric>("pageViews");
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [googleStatus, setGoogleStatus] = useState<GoogleStatus | null>(null);
  const [googleMetrics, setGoogleMetrics] = useState<GoogleMetrics | null>(null);
  const [cloudflare, setCloudflare] = useState<CloudflareData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mapPage, setMapPage] = useState("");
  const [mapDevice, setMapDevice] = useState("Mobile");
  const [fetchedGrid, setFetchedGrid] = useState<ClickGrid | undefined>(undefined);

  // La mappa dei clic usa i dati già caricati; se cambi pagina o dispositivo li rilegge.
  const wantedMapPage = mapPage || data?.clickGrid?.page || "";
  const initialGridMatches = Boolean(data?.clickGrid && data.clickGrid.page === wantedMapPage && data.clickGrid.device === mapDevice);
  const mapGrid = initialGridMatches ? data?.clickGrid
    : fetchedGrid && fetchedGrid.page === wantedMapPage && fetchedGrid.device === mapDevice ? fetchedGrid : undefined;
  useEffect(() => {
    if (!data || !wantedMapPage || initialGridMatches) return;
    let active = true;
    api<AnalyticsData>(`/api/sites/${site.id}/analytics?range=${range}&clickPage=${encodeURIComponent(wantedMapPage)}&clickDevice=${mapDevice}`)
      .then((result) => { if (active) setFetchedGrid(result.clickGrid); })
      .catch(() => { if (active) setFetchedGrid(undefined); });
    return () => { active = false; };
  }, [data, wantedMapPage, mapDevice, initialGridMatches, range, site.id]);

  useEffect(() => {
    let active = true;
    Promise.allSettled([
      api<AnalyticsData>(`/api/sites/${site.id}/analytics?range=${range}`),
      api<GoogleStatus>(`/api/sites/${site.id}/integrations/google`),
      api<CloudflareData>(`/api/sites/${site.id}/integrations/cloudflare`),
    ]).then(async ([analyticsResult, googleResult, cloudflareResult]) => {
      if (!active) return;
      if (analyticsResult.status === "fulfilled") {
        setData(analyticsResult.value);
        setSelectedDate((current) => current && analyticsResult.value.days.some((day) => day.date === current)
          ? current
          : analyticsResult.value.freshness.lastRecordedAt || analyticsResult.value.days.at(-1)?.date || "");
      } else setError(analyticsResult.reason instanceof Error ? analyticsResult.reason.message : "Statistiche non disponibili");
      if (googleResult.status === "fulfilled") {
        setGoogleStatus(googleResult.value);
        if (googleResult.value.connected) {
          try { setGoogleMetrics(await api<GoogleMetrics>(`/api/sites/${site.id}/integrations/google/metrics`)); } catch { setGoogleMetrics(null); }
        }
      }
      if (cloudflareResult.status === "fulfilled") setCloudflare(cloudflareResult.value);
      setLoading(false);
    });
    return () => { active = false; };
  }, [range, site.id]);

  const chartPoints = useMemo<ChartPoint[]>(() => {
    if (!data) return [];
    const bucketSize = range === 365 ? 7 : range === 90 ? 3 : 1;
    const points: ChartPoint[] = [];
    for (let index = 0; index < data.days.length; index += bucketSize) {
      const days = data.days.slice(index, index + bucketSize);
      points.push({
        key: `${days[0].date}-${days.at(-1)!.date}`,
        days,
        pageViews: days.reduce((total, day) => total + day.pageViews, 0),
        visitors: days.reduce((total, day) => total + day.visitors, 0),
      });
    }
    return points;
  }, [data, range]);
  const selectedPoint = chartPoints.find((point) => point.days.some((day) => day.date === selectedDate)) || chartPoints.at(-1) || null;
  const max = Math.max(1, ...chartPoints.map((point) => point[metric]));
  const viewsPerVisitor = data?.totals.visitors ? data.totals.pageViews / data.totals.visitors : 0;
  const previousViewsPerVisitor = data?.previous.visitors ? data.previous.pageViews / data.previous.visitors : 0;
  const bestDay = useMemo(() => data?.days.reduce<AnalyticsDay | null>((best, day) => !best || day.pageViews > best.pageViews ? day : best, null) || null, [data]);
  const hasTraffic = Boolean(data?.totals.pageViews);

  return <div className="analytics-page">
    <header className="analytics-hero">
      <div className="analytics-hero-copy">
        <p className="eyebrow">Universe intelligence</p>
        <h2>Capire il sito.<br /><span>Decidere meglio.</span></h2>
        <p>Traffico, pubblico e visibilità organica in un’unica lettura. Ogni numero può essere approfondito giorno per giorno.</p>
      </div>
      <div className="analytics-range" aria-label="Periodo delle statistiche">
        <small>Periodo</small>
        <div>{([7, 30, 90, 365] as RangeDays[]).map((days) => <button key={days} className={range === days ? "active" : ""} onClick={() => { setLoading(true); setError(""); setRange(days); }}>{days === 365 ? "1 anno" : `${days} gg`}</button>)}</div>
        <span>{data ? `${displayDate(data.range.startDate, dateShort)} - ${displayDate(data.range.endDate, dateShort)}` : "Caricamento periodo"}</span>
      </div>
    </header>

    <SiteSummary siteId={site.id} onOpenSeo={onOpenSeo} />

    {error && <div className="notice error">{error}</div>}
    {loading && !data ? <div className="analytics-loading"><i /><i /><i /><i /></div> : data && <>
      <section className="analytics-kpis" aria-label="Indicatori principali">
        <div role="button" tabIndex={0} aria-pressed={metric === "pageViews"} className={metric === "pageViews" ? "active" : ""} onClick={() => setMetric("pageViews")} onKeyDown={(event) => { if ((event.key === "Enter" || event.key === " ") && event.target === event.currentTarget) { event.preventDefault(); setMetric("pageViews"); } }}>
          <small>Visualizzazioni <InfoTip label="Visualizzazioni" text="Quante pagine del sito sono state aperte. Una persona può generarne più di una." /></small><strong>{integer.format(data.totals.pageViews)}</strong><Change current={data.totals.pageViews} previous={data.previous.pageViews} /><i>Apri andamento</i>
        </div>
        <div role="button" tabIndex={0} aria-pressed={metric === "visitors"} className={metric === "visitors" ? "active" : ""} onClick={() => setMetric("visitors")} onKeyDown={(event) => { if ((event.key === "Enter" || event.key === " ") && event.target === event.currentTarget) { event.preventDefault(); setMetric("visitors"); } }}>
          <small>Visitatori giornalieri <InfoTip label="Visitatori" text="Persone distinte che hanno aperto il sito in un giorno." /></small><strong>{integer.format(data.totals.visitors)}</strong><Change current={data.totals.visitors} previous={data.previous.visitors} /><i>Apri andamento</i>
        </div>
        <article><small>Pagine per visitatore <InfoTip label="Pagine per visitatore" text="In media quante pagine apre una persona in una visita." /></small><strong>{number.format(viewsPerVisitor)}</strong><Change current={viewsPerVisitor} previous={previousViewsPerVisitor} /><i>Profondità media di visita</i></article>
        <article><small>Giorno più attivo</small><strong className="date-value">{bestDay && bestDay.pageViews ? displayDate(bestDay.date, dateShort) : "—"}</strong><span className="analytics-change neutral">{bestDay?.pageViews ? `${integer.format(bestDay.pageViews)} visualizzazioni` : "In attesa di visite"}</span><i>Picco del periodo</i></article>
      </section>

      <section className="analytics-chart-card">
        <header>
          <div><p className="eyebrow indigo">Andamento</p><h3>{metric === "pageViews" ? "Visualizzazioni" : "Visitatori"} {range <= 30 ? "giornalieri" : range === 90 ? "ogni 3 giorni" : "settimanali"}</h3></div>
          <div className="analytics-selected-day"><small>{selectedPoint ? displayPeriod(selectedPoint.days) : "Seleziona un periodo"}</small><strong>{integer.format(selectedPoint?.[metric] || 0)}</strong><span>{metric === "pageViews" ? "visualizzazioni" : "visitatori"}</span></div>
        </header>
        <div className={`analytics-bars range-${range}`} role="group" aria-label="Seleziona una colonna per leggere il dato esatto">
          {chartPoints.map((point) => <button key={point.key} className={selectedPoint?.key === point.key ? "selected" : ""} onClick={() => setSelectedDate(point.days.at(-1)!.date)} aria-label={`${displayPeriod(point.days)}: ${point[metric]}`}>
            <span><i style={{ height: `${Math.max(point[metric] ? 4 : 1, point[metric] / max * 100)}%` }} /></span>
            {range <= 30 && <small>{displayDate(point.days[0].date, dateShort)}</small>}
          </button>)}
        </div>
        {range > 30 && chartPoints.length > 0 && <div className="analytics-axis" aria-hidden="true"><span>{displayDate(data.range.startDate, dateShort)}</span><span>{displayDate(chartPoints[Math.floor(chartPoints.length / 2)].days[0].date, dateShort)}</span><span>{displayDate(data.range.endDate, dateShort)}</span></div>}
        <footer><span><i /> {metric === "pageViews" ? "Visualizzazioni" : "Visitatori"}</span><p>Clicca una colonna per il dato esatto. {range === 90 ? "Ogni colonna riunisce 3 giorni." : range === 365 ? "Ogni colonna riunisce una settimana." : "I giorni senza traffico restano visibili."}</p></footer>
      </section>

      {!hasTraffic && <section className="analytics-zero-state"><span>00</span><div><h3>Il tracciamento è attivo</h3><p>Il grafico si popolerà con le prossime visite reali. Universe non inserisce dati dimostrativi nel pannello dei clienti.</p></div></section>}

      <div className="analytics-breakdown-grid">
        <Breakdown title="Pagine più viste" subtitle="I contenuti che attirano più attenzione" items={data.breakdowns.pages} empty="Le nuove visite verranno suddivise per pagina, senza modificare il sito." />
        <Breakdown title="Dispositivi" subtitle="Mobile, desktop e tablet" items={data.breakdowns.devices} empty="La tipologia di dispositivo viene rilevata in forma aggregata." />
        <Breakdown title="Origine del traffico" subtitle="Da dove arrivano le persone" items={data.breakdowns.sources} empty="Google, social, WhatsApp e accessi diretti compariranno qui." />
        <Breakdown title="Paesi" subtitle="Distribuzione geografica anonima" items={data.breakdowns.countries} empty="La posizione viene conservata soltanto a livello di Paese." />
      </div>

      <ChannelSection data={data} />

      <GoogleAdsPanel siteId={site.id} range={range} />

      <div className="analytics-breakdown-grid analytics-behaviour">
        <Breakdown title="Campagne" subtitle="Annunci con nome campagna nell'indirizzo (utm_campaign)" items={(data.campaigns || []).map((item) => ({ label: `${item.channel} · ${item.campaign}`, pageViews: item.pageViews }))} empty="Se negli annunci usi utm_campaign, qui vedrai quali campagne portano più visite." />
        <Breakdown title="Cosa cliccano" subtitle="Gli elementi più toccati del sito" items={(data.clicks || []).map((item) => ({ label: `${CLICK_KIND_LABEL[item.kind] || item.kind} · ${item.target}`, pageViews: item.clicks }))} empty="Telefono, WhatsApp, pulsanti e link compariranno qui con il numero di clic." />
      </div>

      <ClickMap grid={mapGrid} pages={data.clickPages || []} device={mapDevice} page={mapPage || data.clickGrid?.page || ""}
        onDevice={setMapDevice} onPage={setMapPage} />

      <section className="analytics-connections">
        <header><div><p className="eyebrow indigo">Fonti collegate</p><h3>Un solo pannello, dati verificabili</h3></div><p>{data.privacy}</p></header>
        <div>
          <article className="connected"><span>U</span><div><strong>Universe Analytics</strong><small>Traffico proprietario e anonimo</small></div><em>Attivo</em></article>
          <article className={cloudflare?.analyticsStatus === "available" ? "connected" : ""}><span>CF</span><div><strong>Cloudflare</strong><small>{cloudflare?.analyticsStatus === "available" ? "Traffico infrastrutturale disponibile" : "Integrazione tecnica gestita da Universe"}</small></div><em>{cloudflare?.analyticsStatus === "available" ? "Dati attivi" : cloudflare?.configured ? "Gestito" : "Da configurare"}</em></article>
          <button className={googleStatus?.connected ? "connected" : ""} onClick={onOpenSeo}><span>G</span><div><strong>Google Search Console</strong><small>{googleMetrics ? `${integer.format(googleMetrics.totals.clicks)} clic · ${integer.format(googleMetrics.totals.impressions)} impressioni` : googleStatus?.configured ? "Il cliente può autorizzare il proprio account" : "Prima configurazione a cura di Universe"}</small></div><em>{googleStatus?.connected ? "Apri" : googleStatus?.configured ? "Autorizza" : "Setup"}</em></button>
        </div>
        <p className="analytics-connection-note"><strong>Come funziona:</strong> Cloudflare viene gestito centralmente da Universe. Google richiede invece un’autorizzazione del proprietario del sito e resta collegato in sola lettura.</p>
      </section>
    </>}
  </div>;
}
